import React, { forwardRef } from 'react';

const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;

// Ultra-Premium Typography
const FONTS = {
  sans: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  serif: '"Playfair Display", "Didot", "Georgia", serif'
};

const COND_LABELS = {
  acne: 'Acne & Blemishes',
  pigmentation: 'Hyperpigmentation',
  pores: 'Enlarged Pores',
  wrinkles: 'Fine Lines',
  darkCircles: 'Dark Circles',
  eyeWrinkles: "Crow's Feet",
  eyeBags: 'Under-Eye Bags',
  deepWrinkles: 'Deep Wrinkles'
};

const USER_CONCERN_LABELS = {
  acne: 'Acne',
  dull_skin: 'Dull Skin',
  pigmentation: 'Pigmentation',
  acne_scar: 'Acne Scars',
  open_pores: 'Open Pores',
  normal_skin: 'General Checkup',
  uneven_skin_tone: 'Uneven Skin Tone',
  wrinkles: 'Wrinkles',
  loose_skin: 'Loose Skin',
  dark_circles: 'Dark Circles'
};

const CONDITION_ALIASES = {
  pigmentation: 'Pigmentation',
  hyperpigmentation: 'Pigmentation',
  pigimnation: 'Pigmentation',
  pigmentaion: 'Pigmentation',
  pores: 'Pores',
  pors: 'Pores',
  porse: 'Pores',
  poers: 'Pores',
  acne: 'Acne',
  acnee: 'Acne',
  ance: 'Acne',
  darkcircles: 'Dark Circles',
  darkcirlces: 'Dark Circles',
  drakcircles: 'Dark Circles',
  finelines: 'Fine Lines',
  finline: 'Fine Lines',
  deepwrinkles: 'Deep Wrinkles',
  deapwrinkles: 'Deep Wrinkles',
  crowsfeet: "Crow's Feet",
  crowfeet: "Crow's Feet",
  eyebags: 'Eye Bags',
  eybags: 'Eye Bags',
  skintexture: 'Skin Texture',
  hydration: 'Hydration'
};

function demotePores(sortedConditions) {
  const idx = sortedConditions.findIndex(([k]) => k === 'pores');
  if (idx >= 0 && idx < 4) {
    const [entry] = sortedConditions.splice(idx, 1);
    sortedConditions.splice(4, 0, entry);
  }
  return sortedConditions;
}

function clampText(value, maxChars) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars);
  const safe = slice.includes(' ') ? slice.slice(0, slice.lastIndexOf(' ')) : slice;
  return `${safe.trim()}…`;
}

function cleanText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeConcernText(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const compact = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  return CONDITION_ALIASES[compact] || raw;
}

function getAdaptivePageOneLayout(summaryText, concernCount) {
  const length = String(summaryText || '').length;
  const concerns = Number(concernCount) || 0;

  if (length > 300 || concerns >= 3) {
    return {
      topSectionMarginBottom: 40,
      kickerMarginBottom: 18,
      summarySectionMarginBottom: 22,
      summaryFontSize: 12,
      summaryLineHeight: '1.34em',
      severityFontSize: 40,
      concernLabelMarginBottom: 4,
      concernChipMarginBottom: 4,
      concernChipHorizontalPadding: 10,
      concernChipHeight: 20,
      concernChipFontSize: 9,
      maxConcerns: 4
    };
  }

  if (length > 220 || concerns >= 2) {
    return {
      topSectionMarginBottom: 48,
      kickerMarginBottom: 22,
      summarySectionMarginBottom: 28,
      summaryFontSize: 14,
      summaryLineHeight: '1.42em',
      severityFontSize: 44,
      concernLabelMarginBottom: 6,
      concernChipMarginBottom: 6,
      concernChipHorizontalPadding: 13,
      concernChipHeight: 24,
      concernChipFontSize: 10,
      maxConcerns: 4
    };
  }

  return {
    topSectionMarginBottom: 64,
    kickerMarginBottom: 32,
    summarySectionMarginBottom: 48,
    summaryFontSize: 17,
    summaryLineHeight: '1.6em',
    severityFontSize: 48,
    concernLabelMarginBottom: 12,
    concernChipMarginBottom: 12,
    concernChipHorizontalPadding: 14,
    concernChipHeight: 26,
    concernChipFontSize: 11,
    maxConcerns: 4
  };
}

const getSeverity = (theme, severity) => {
  const x = String(severity || '').toLowerCase();
  if (x === 'high') return { label: 'Severe', color: '#991B1B', dot: '#B91C1C', bg: '#FEF2F2' };
  if (x === 'low') return { label: 'Mild', color: '#065F46', dot: '#10B981', bg: '#ECFDF5' };
  return { label: 'Moderate', color: '#92400E', dot: '#F59E0B', bg: '#FFFBEB' };
};

const LabelValue = ({ label, value, valueStyle }) => (
  <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 28 }}>
    <div
      style={{
        fontFamily: FONTS.sans,
        fontSize: 9,
        fontWeight: 600,
        color: '#8A8A8A',
        textTransform: 'uppercase',
        letterSpacing: 1.2,
        marginBottom: 6
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: FONTS.serif, fontSize: 16, color: '#1A1A1A', fontWeight: 400, ...valueStyle }}>{value}</div>
  </div>
);

const SectionKicker = ({ title, subtitle, marginBottom = 32 }) => (
  <div style={{ marginBottom, display: 'flex', flexDirection: 'column' }}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: subtitle ? 6 : 0 }}>
      <div style={{ fontFamily: FONTS.sans, fontSize: 10, fontWeight: 600, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: 2, marginRight: 16 }}>
        {title}
      </div>
      <div style={{ flex: 1, height: 1, backgroundColor: '#E5E5E5' }} />
    </div>
    {subtitle && (
      <div style={{ fontFamily: FONTS.serif, fontSize: 14, color: '#737373' }}>
        {subtitle}
      </div>
    )}
  </div>
);

const ConditionBar = ({ label, percentage, severity, theme }) => {
  const score = Math.max(0, Math.min(100, Number(percentage) || 0));
  const sev = getSeverity(theme, severity);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 6 }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500, color: '#1A1A1A', letterSpacing: 0.2 }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            fontFamily: FONTS.sans, fontSize: 9, fontWeight: 600, color: sev.color, 
            textTransform: 'uppercase', letterSpacing: 1, marginRight: 12
          }}>
            {sev.label}
          </span>
          <span style={{ fontFamily: FONTS.serif, fontSize: 15, fontWeight: 400, color: '#1A1A1A', minWidth: 36, textAlign: 'right' }}>
            {score}%
          </span>
        </div>
      </div>
      <div style={{ width: '100%', height: 2, backgroundColor: '#F0F0F0', position: 'relative' }}>
        <div style={{ width: `${score}%`, height: '100%', backgroundColor: sev.dot, position: 'absolute', top: 0, left: 0 }} />
      </div>
    </div>
  );
};

const RoutineStep = ({ step, index }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
    <div style={{
      fontFamily: FONTS.sans,
      fontSize: 10,
      fontWeight: 500,
      color: '#A3A3A3',
      borderBottom: '1px solid #A3A3A3',
      paddingBottom: 2,
      marginTop: 2,
      marginRight: 16
    }}>
      0{index + 1}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: FONTS.serif, fontSize: 14, color: '#1A1A1A', lineHeight: '1.45em' }}>
        {step || '—'}
      </div>
    </div>
  </div>
);

const RoutineColumn = ({ title, steps }) => (
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
    <div
      style={{
        fontFamily: FONTS.sans,
        fontSize: 11,
        fontWeight: 600,
        color: '#1A1A1A',
        textTransform: 'uppercase',
        letterSpacing: 1.5,
        marginBottom: 22,
        paddingBottom: 12,
        borderBottom: '1px solid #1A1A1A'
      }}
    >
      {title}
    </div>
    <div style={{ flex: 1 }}>
      {steps.map((step, i) => (
        <RoutineStep key={`${title}-${i}`} index={i} step={step} />
      ))}
    </div>
  </div>
);

const ReportHeader = ({ config, reportId, date }) => (
  <div
    style={{
      height: 120,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: '1px solid #E5E5E5',
      marginBottom: 40,
      paddingBottom: 10
    }}
  >
    <img
      src={config.pdfLogoUrl || config.logoUrl}
      alt={config.logoAlt}
      crossOrigin="anonymous"
      style={{ height: 56, maxWidth: 240, width: 'auto', objectFit: 'contain', objectPosition: 'left center', display: 'block' }}
    />

    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#8A8A8A', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
        Confidential Report
      </div>
      <div style={{ fontFamily: FONTS.serif, fontSize: 16, color: '#1A1A1A', marginBottom: 6 }}>{reportId}</div>
      <div style={{ fontFamily: FONTS.sans, fontSize: 11, color: '#737373', letterSpacing: 0.5 }}>{date}</div>
    </div>
  </div>
);

const ReportFooter = ({ page, config }) => (
  <div
    style={{
      height: 80,
      flexShrink: 0,
      marginTop: 'auto',
      borderTop: '1px solid #E5E5E5',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: FONTS.sans,
      fontSize: 10,
      color: '#8A8A8A',
      letterSpacing: 1,
      textTransform: 'uppercase'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span style={{ color: '#1A1A1A', fontWeight: 500, marginRight: 24 }}>{config.footerName}</span>
      <span style={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#D4D4D4', marginRight: 24 }} />
      <span>{clampText(config.pdfClinicName, 45)}</span>
    </div>
    <div style={{ fontWeight: 500 }}>PAGE {page} / 3</div>
  </div>
);

const PageFrame = ({ page, reportId, date, config, children }) => (
  <div
    style={{
      width: PAGE_WIDTH,
      height: PAGE_HEIGHT,
      backgroundColor: '#FAFAF9',
      padding: '48px',
      boxSizing: 'border-box'
    }}
    data-pdf-page={String(page)}
  >
    <div style={{
      width: '100%',
      height: '100%',
      backgroundColor: '#FFFFFF',
      border: '1px solid #E5E5E5',
      padding: '0 48px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <ReportHeader config={config} reportId={reportId} date={date} />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        {children}
      </div>
      <ReportFooter page={page} config={config} />
    </div>
  </div>
);

const UnifiedPDFPreview = forwardRef(({ analysisData, patientData, capturedImage, annotatedImage, config }, ref) => {
  if (!analysisData || !patientData || !config) {
    return <div ref={ref} />;
  }

  const THEME = {
    primary: '#1A1A1A',
    secondary: '#737373',
    accent: config.pdfAccent || '#1A1A1A',
    surface: '#FAFAF9',
    white: '#FFFFFF',
    border: '#E5E5E5',
    text: '#1A1A1A'
  };

  const now = new Date();
  const date = `${now.getDate()} ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][now.getMonth()]} ${now.getFullYear()}`;
  const reportId = `${config.reportIdPrefix || 'MSA'}-${String(Date.now()).slice(-6)}`;

  const conds = demotePores(
    Object.entries(analysisData?.detailedAnalysis || {})
      .filter(([key]) => COND_LABELS[key])
      .sort((a, b) => (Number(b[1]?.percentage) || 0) - (Number(a[1]?.percentage) || 0))
  );

  const top = conds[0];
  const topKey = top?.[0];
  const topPercent = Number(top?.[1]?.percentage) || 0;
  const overall = getSeverity(THEME, analysisData?.overallAssessment?.severity);

  const age = Number(patientData?.age) || 25;
  const patientConcern = USER_CONCERN_LABELS[patientData?.primaryConcern] || 'General Checkup';
  const identifiedConcern = `${COND_LABELS[topKey] || 'None'} (${topPercent}%)`;

  const summaryFallback = `The analysis indicates ${patientData?.name || 'the patient'}'s primary skin concern is ${COND_LABELS[topKey] || 'general skin health'}, with related secondary findings requiring a guided skincare plan.`;
  const rawSummaryText = analysisData?.summary || summaryFallback;
  const rawKeyConcerns = (analysisData?.overallAssessment?.primaryConcerns || [])
    .map(item => normalizeConcernText(item))
    .filter(Boolean)
    .filter((item, index, arr) => arr.indexOf(item) === index);

  const skincareText = analysisData?.recommendations?.skincare || '';
  const parseRoutine = (regex, fallback) => {
    const match = skincareText.match(regex);
    if (!match) return fallback;
    const parsed = match[1]
      .split(/[,;\n]/)
      .map(step => cleanText(step))
      .filter(Boolean)
      .slice(0, 4);
    return parsed.length ? parsed : fallback;
  };

  const morningSteps = parseRoutine(/morning[:\s]*([^.]+)/i, ['Gentle cleanser', 'Niacinamide serum', 'Gel moisturizer', 'SPF 30+']);
  const eveningSteps = parseRoutine(/evening[:\s]*([^.]+)/i, ['Salicylic acid cleanser', 'Targeted treatment', 'Repair moisturizer', 'Spot care']);

  while (morningSteps.length < 4) morningSteps.push('');
  while (eveningSteps.length < 4) eveningSteps.push('');

  const followupText = cleanText(analysisData?.recommendations?.followup || '');
  const disclaimerText = cleanText(analysisData?.disclaimer || config.pdfDisclaimer);
  const pageOneLayout = getAdaptivePageOneLayout(rawSummaryText, rawKeyConcerns.length);
  const summaryText = cleanText(rawSummaryText);
  const keyConcerns = rawKeyConcerns.slice(0, pageOneLayout.maxConcerns);
  const specialistName = config.pdfSpecialistName || config.footerName;
  const specialistQualifications = cleanText(config.pdfSpecialistQualifications || '');
  const specialistOrganization = cleanText(config.pdfSpecialistOrganization || config.pdfClinicName);
  const contactAddressLines = cleanText(config.pdfAddress || '')
    .split(/\s*•\s*/)
    .map(line => cleanText(line))
    .filter(Boolean);

  return (
    <div ref={ref} style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT * 3, backgroundColor: '#D4D4D4' }}>
      
      {/* PAGE 1: EXECUTIVE SUMMARY */}
      <PageFrame page={1} config={config} reportId={reportId} date={date}>
        
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontFamily: FONTS.serif, fontSize: 46, color: '#1A1A1A', fontWeight: 400, marginBottom: 16, lineHeight: '1.1em' }}>
            Dermatological Assessment
          </div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: '#737373', letterSpacing: 0.5, lineHeight: '1.6em', maxWidth: 500 }}>
            A comprehensive, AI-assisted analysis of skin health, texture, and underlying conditions.
          </div>
        </div>

        <div style={{ display: 'flex', marginBottom: pageOneLayout.topSectionMarginBottom }}>
          <div style={{
            width: 200, height: 260, flexShrink: 0,
            padding: 8, border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF',
            marginRight: 48, display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ 
              flex: 1, 
              backgroundColor: '#F5F5F5', 
              marginBottom: 8,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {capturedImage?.dataUrl ? (
                <img
                  src={capturedImage.dataUrl}
                  alt="Captured skin scan"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                    flexShrink: 0,
                    filter: 'contrast(1.05) grayscale(0.1)'
                  }}
                  crossOrigin="anonymous"
                />
              ) : null}
            </div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: '#737373' }}>
              Estimated Skin Age: {analysisData?.overallAssessment?.skinAge || age}
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontFamily: FONTS.serif, fontSize: 36, color: '#1A1A1A', fontWeight: 400, marginBottom: 32, borderBottom: '1px solid #E5E5E5', paddingBottom: 24 }}>
              {clampText(patientData?.name || 'Patient', 30)}
            </div>

            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, paddingRight: 24 }}>
                <LabelValue label="Age" value={`${age} Years`} />
                <LabelValue label="Primary Concern" value={patientConcern} />
              </div>
              <div style={{ flex: 1, paddingLeft: 24 }}>
                <LabelValue label="Skin Type" value={analysisData?.patientAnalysis?.skinType || 'Combination'} />
                <LabelValue label="Identified Focus" value={identifiedConcern} valueStyle={{ color: overall.color }} />
              </div>
            </div>
          </div>
        </div>

        <SectionKicker title="Clinical Evaluation" marginBottom={pageOneLayout.kickerMarginBottom} />

        <div style={{ display: 'flex', marginBottom: pageOneLayout.summarySectionMarginBottom }}>
          <div style={{ flex: 1, marginRight: 48 }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 16 }}>
              Physician's Summary
            </div>
            <div style={{ fontFamily: FONTS.serif, fontSize: pageOneLayout.summaryFontSize, color: '#1A1A1A', lineHeight: pageOneLayout.summaryLineHeight }}>
              "{summaryText}"
            </div>
          </div>
          
          <div style={{ width: 1, backgroundColor: '#E5E5E5', marginRight: 48 }} />

          <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
              Severity Index
            </div>
            <div style={{ fontFamily: FONTS.sans, fontSize: pageOneLayout.severityFontSize, color: overall.color, fontWeight: 300, lineHeight: '1em', letterSpacing: -1 }}>
              {analysisData?.overallAssessment?.severity || 'Moderate'}
            </div>
          </div>
        </div>

        {keyConcerns.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginRight: 16, marginBottom: pageOneLayout.concernLabelMarginBottom }}>
              Noted Conditions:
            </span>
            {keyConcerns.map((concern, idx) => (
              <span key={idx} style={{
                fontFamily: FONTS.sans, fontSize: pageOneLayout.concernChipFontSize, color: '#1A1A1A',
                border: '1px solid #E5E5E5',
                padding: `0 ${pageOneLayout.concernChipHorizontalPadding}px`,
                height: pageOneLayout.concernChipHeight,
                borderRadius: 20,
                marginRight: 12, marginBottom: pageOneLayout.concernChipMarginBottom,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 'normal',
                boxSizing: 'border-box', whiteSpace: 'nowrap', verticalAlign: 'middle',
                flexShrink: 0
              }}>
                {concern}
              </span>
            ))}
          </div>
        )}

      </PageFrame>

      {/* PAGE 2: CONDITION ANALYSIS */}
      <PageFrame page={2} config={config} reportId={reportId} date={date}>
        <SectionKicker title="Diagnostic Mapping" subtitle="Detailed breakdown of detected skin conditions and their respective severity." />

        <div style={{ display: 'flex', marginBottom: 48, height: 440 }}>
          <div style={{
            width: 300, flexShrink: 0,
            padding: 8, border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF',
            display: 'flex', flexDirection: 'column', marginRight: 48
          }}>
            <div style={{ 
              flex: 1, 
              backgroundColor: '#FAFAF9', 
              position: 'relative', 
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {(annotatedImage || capturedImage?.dataUrl) ? (
                <img
                  src={annotatedImage || capturedImage?.dataUrl}
                  alt="Annotated skin mapping"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                    flexShrink: 0,
                    filter: 'contrast(1.05)'
                  }}
                  crossOrigin="anonymous"
                />
              ) : null}
              <div style={{
                position: 'absolute', bottom: 12, left: 12,
                backgroundColor: '#FFFFFF', padding: '6px 12px',
                fontFamily: FONTS.sans, fontSize: 9, color: '#1A1A1A',
                textTransform: 'uppercase', letterSpacing: 1.5,
                border: '1px solid #E5E5E5'
              }}>
                AI Diagnostics
              </div>
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {conds.slice(0, 8).map(([key, value]) => (
              <ConditionBar
                key={`bar-${key}`}
                label={COND_LABELS[key]}
                percentage={value?.percentage}
                severity={value?.severity}
                theme={THEME}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1 }}>
          {conds.slice(0, 3).map(([key, value], index) => {
            const sev = getSeverity(THEME, value?.severity);
            const description = cleanText(value?.description || 'Detected during clinical AI analysis.');
            return (
              <div
                key={`finding-${key}-${index}`}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  paddingTop: 20, borderTop: `2px solid ${sev.dot}`,
                  marginRight: index < 2 ? 24 : 0
                }}
              >
                <div style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
                  Primary Finding 0{index + 1}
                </div>
                <div style={{ fontFamily: FONTS.serif, fontSize: 20, color: '#1A1A1A', lineHeight: '1.2em', marginBottom: 12 }}>
                  {COND_LABELS[key]}
                </div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 11.5, color: '#737373', lineHeight: '1.55em', flex: 1 }}>
                  {description}
                </div>
              </div>
            );
          })}
        </div>
      </PageFrame>

      {/* PAGE 3: ACTION PLAN */}
      <PageFrame page={3} config={config} reportId={reportId} date={date}>
        <SectionKicker title="Prescribed Regimen" subtitle="A curated protocol designed to address the identified conditions." />

        <div style={{ display: 'flex', marginBottom: 48, flex: 1 }}>
          <RoutineColumn title="Morning Protocol" steps={morningSteps} />
          <div style={{ width: 1, backgroundColor: '#E5E5E5', marginLeft: 48, marginRight: 48 }} />
          <RoutineColumn title="Evening Protocol" steps={eveningSteps} />
        </div>

        {followupText ? (
          <div style={{ marginBottom: 48 }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#1A1A1A', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 16 }}>
              Follow-up Directives
            </div>
            <div style={{ 
              padding: 24, 
              backgroundColor: '#FAFAF9', 
              border: '1px solid #E5E5E5'
            }}>
              <div style={{ fontFamily: FONTS.serif, fontSize: 15, color: '#1A1A1A', lineHeight: '1.6em' }}>
                {followupText}
              </div>
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: 'auto' }}>
          <div
            style={{
              backgroundColor: THEME.accent === '#1A1A1A' ? '#1A1A1A' : THEME.accent,
              color: '#FFFFFF',
              padding: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 20
            }}
          >
            <div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 9, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                Attending Specialist
              </div>
              <div style={{ fontFamily: FONTS.serif, fontSize: 32, lineHeight: '1.1em', marginBottom: 8 }}>
                {specialistName}
              </div>
              {specialistQualifications ? (
                <div style={{ fontFamily: FONTS.sans, fontSize: 11, opacity: 0.88, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
                  {specialistQualifications}
                </div>
              ) : null}
              <div style={{ fontFamily: FONTS.sans, fontSize: 12, opacity: 0.9, letterSpacing: 0.5 }}>
                {specialistOrganization}
              </div>
            </div>

            <div style={{ width: 1, height: 60, backgroundColor: 'rgba(255,255,255,0.2)' }} />

            <div style={{ textAlign: 'right', width: 236, flexShrink: 0, marginLeft: 32 }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 9, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                Contact
              </div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 20, fontWeight: 300, letterSpacing: 1, marginBottom: 8 }}>
                {config.pdfPhone}
              </div>
              <div style={{ fontFamily: FONTS.sans, fontSize: 11, opacity: 0.9, lineHeight: '1.55em' }}>
                {contactAddressLines.map((line, index) => (
                  <div key={`contact-line-${index}`}>{line}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', padding: '0 24px 6px' }}>
          <div style={{ display: 'inline-block', maxWidth: '100%', fontFamily: FONTS.sans, fontSize: 9, color: '#A3A3A3', lineHeight: '1.6em', paddingBottom: 2 }}>
            <span style={{ textTransform: 'uppercase', letterSpacing: 1 }}>Disclaimer:</span> {disclaimerText}
          </div>
        </div>
      </PageFrame>
    </div>
  );
});

UnifiedPDFPreview.displayName = 'UnifiedPDFPreview';

export default UnifiedPDFPreview;
